import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RouterLink } from '../../utils/linkNav';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Send,
  CheckCircle,
  HighlightOff,
  Block,
  PlayArrow,
  DoneAll,
  Description,
  Assignment,
} from '@mui/icons-material';
import {
  PageHeader,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  CodeCell,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { api } from '../../services/apiClient';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';
import { SwoStatus, SWO_STATUS_TONE } from './ServiceWorkOrderPage';

const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

// The lifecycle a non-cancelled work order walks through, left to right.
const SWO_LIFECYCLE: SwoStatus[] = ['DRAFT', 'SUBMITTED', 'APPROVED', 'IN_PROGRESS', 'COMPLETED'];

type SesStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

const SES_STATUS_TONE: Record<SesStatus, StatusTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  APPROVED: 'success',
  REJECTED: 'error',
  CANCELLED: 'neutral',
};

interface SwoItem {
  id?: number;
  description: string;
  quantity: number | string;
  uom?: string;
  rate: number | string;
  amount?: number | string;
  sac_code?: string;
}

interface LinkedSes {
  id: number;
  ses_number: string;
  status: SesStatus;
  po_id?: number | null;
  vendor_id?: number | string;
  service_period_from?: string;
  service_period_to?: string;
  total_amount: number | string;
  created_at?: string;
}

interface SwoDetail {
  id: number;
  swo_number: string;
  vendor_id: number | string;
  vendor_name: string;
  service_category: string;
  plant?: string;
  department?: string;
  cost_center_name?: string;
  project_name?: string;
  wo_date: string;
  start_date?: string | null;
  end_date?: string | null;
  scope_of_work?: string;
  payment_terms?: string;
  contract_reference?: string;
  sla?: string;
  warranty?: string;
  remarks?: string;
  estimated_value: number | string;
  tax_amount: number | string;
  total_value: number | string;
  status: SwoStatus;
  items: SwoItem[];
  serviceEntrySheets?: LinkedSes[];
}

// Reject/Cancel both need a recorded reason. Reused for either, the way
// ServiceEntryDetailPage's reject dialog is the model for both actions here.
const ReasonDialog: React.FC<{
  open: boolean;
  title: string;
  helperText: string;
  submitLabel: string;
  loading: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}> = ({ open, title, helperText, submitLabel, loading, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>{helperText}</DialogContentText>
        <TextField
          label="Reason"
          fullWidth
          multiline
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => onSubmit(reason.trim())}
          disabled={reason.trim().length < 3 || loading}
        >
          {loading ? 'Working...' : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ServiceWorkOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<SwoDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { serviceWorkOrder } = await api.get<{ serviceWorkOrder: SwoDetail }>(`/service-work-orders/${id}`);
      setOrder(serviceWorkOrder);
    } catch (err: any) {
      setError(err.message || 'Failed to load service work order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/service-work-orders')} sx={{ mb: 2 }}>
          Back to Service Work Orders
        </Button>
        <Alert severity="error">{error || 'Service work order not found'}</Alert>
      </Box>
    );
  }

  const canEdit = order.status === 'DRAFT';
  const canSubmit = order.status === 'DRAFT';
  const canCancel = order.status === 'DRAFT';
  const canApproveReject = order.status === 'SUBMITTED';
  const canStart = order.status === 'APPROVED';
  const canComplete = order.status === 'IN_PROGRESS';
  const noActionsAvailable = !canEdit && !canSubmit && !canCancel && !canApproveReject && !canStart && !canComplete;

  const runAction = async (name: string, fn: () => Promise<void>) => {
    setActionLoading(name);
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err.message || `Failed to ${name}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmit = () =>
    runAction('submit', async () => {
      await api.post(`/service-work-orders/${id}/submit`);
      setNotice('Service work order submitted for approval.');
      await load();
    });

  const handleApprove = () =>
    runAction('approve', async () => {
      await api.post(`/service-work-orders/${id}/approve`);
      setNotice('Service work order approved.');
      await load();
    });

  const handleReject = (reason: string) =>
    runAction('reject', async () => {
      await api.post(`/service-work-orders/${id}/reject`, { reason });
      setRejectOpen(false);
      setNotice('Service work order rejected and returned to draft.');
      await load();
    });

  const handleStart = () =>
    runAction('start', async () => {
      await api.post(`/service-work-orders/${id}/start`);
      setNotice('Work started.');
      await load();
    });

  const handleComplete = () =>
    runAction('complete', async () => {
      await api.post(`/service-work-orders/${id}/complete`);
      setNotice('Service work order marked complete.');
      await load();
    });

  const handleCancel = (reason: string) =>
    runAction('cancel', async () => {
      await api.post(`/service-work-orders/${id}/cancel`, { reason });
      setCancelOpen(false);
      setNotice('Service work order cancelled.');
      await load();
    });

  const sheets = order.serviceEntrySheets || [];

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/procurement/service-work-orders')} sx={{ mb: 1 }}>
        Back to Service Work Orders
      </Button>

      <PageHeader
        title={order.swo_number}
        qualifier={order.vendor_name ? `· ${order.vendor_name}` : undefined}
        subtitle={`${order.service_category || 'General Service'} · Raised ${fmtDate(order.wo_date)}`}
        actions={<StatusDot label={order.status.replace('_', ' ')} tone={SWO_STATUS_TONE[order.status] || 'neutral'} />}
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {/* Status ribbon */}
      {order.status === 'CANCELLED' ? (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          This service work order has been cancelled.
        </Alert>
      ) : (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {SWO_LIFECYCLE.map((stage, idx) => {
              const currentIdx = SWO_LIFECYCLE.indexOf(order.status);
              const reached = currentIdx >= 0 && idx <= currentIdx;
              const isCurrent = stage === order.status;
              return (
                <React.Fragment key={stage}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: isCurrent ? `${'#0284c7'}14` : 'transparent',
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: reached ? (isCurrent ? '#0284c7' : 'success.main') : 'action.disabledBackground',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: isCurrent ? 700 : 600, color: reached ? 'text.primary' : 'text.disabled' }}
                    >
                      {stage.replace('_', ' ')}
                    </Typography>
                  </Box>
                  {idx < SWO_LIFECYCLE.length - 1 && (
                    <Box sx={{ width: 24, height: 2, bgcolor: reached ? 'success.main' : 'action.disabledBackground' }} />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Paper>
      )}

      <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Workflow Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              component={RouterLink}
              to={`/procurement/service-work-orders/${id}/edit`}
            >
              Edit
            </Button>
          )}
          {canSubmit && (
            <Button variant="contained" startIcon={<Send />} disabled={actionLoading !== null} onClick={handleSubmit}>
              {actionLoading === 'submit' ? 'Submitting...' : 'Submit for Approval'}
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Block />}
              disabled={actionLoading !== null}
              onClick={() => setCancelOpen(true)}
            >
              Cancel
            </Button>
          )}
          {canApproveReject && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                disabled={actionLoading !== null}
                onClick={handleApprove}
              >
                {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<HighlightOff />}
                disabled={actionLoading !== null}
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            </>
          )}
          {canStart && (
            <Button variant="contained" startIcon={<PlayArrow />} disabled={actionLoading !== null} onClick={handleStart}>
              {actionLoading === 'start' ? 'Starting...' : 'Start Work'}
            </Button>
          )}
          {canComplete && (
            <Button variant="contained" color="success" startIcon={<DoneAll />} disabled={actionLoading !== null} onClick={handleComplete}>
              {actionLoading === 'complete' ? 'Completing...' : 'Complete'}
            </Button>
          )}
          {noActionsAvailable && order.status !== 'CANCELLED' && (
            <Typography variant="body2" color="text.secondary">
              This work order is {order.status.toLowerCase()} — no further actions are available.
            </Typography>
          )}
        </Box>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Work Order Details
            </Typography>
            <Grid container spacing={1.5}>
              {[
                ['Vendor', order.vendor_name],
                ['Service Category', order.service_category],
                ['Plant', order.plant],
                ['Department', order.department],
                ['Cost Center', order.cost_center_name],
                ['Project', order.project_name],
                ['WO Date', fmtDate(order.wo_date)],
                ['Period', order.start_date || order.end_date ? `${fmtDate(order.start_date)} – ${fmtDate(order.end_date)}` : null],
                ['Payment Terms', order.payment_terms],
                ['Contract Reference', order.contract_reference],
                ['SLA', order.sla],
                ['Warranty', order.warranty],
              ].map(([label, value]) => (
                <Grid size={{ xs: 6, sm: 4 }} key={label as string}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            {order.scope_of_work && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Scope of Work
                </Typography>
                <Typography variant="body2">{order.scope_of_work}</Typography>
              </>
            )}
            {order.remarks && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Remarks
                </Typography>
                <Typography variant="body2">{order.remarks}</Typography>
              </>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Value
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Estimated Value</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatIndianNumber(toNum(order.estimated_value))}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatIndianNumber(toNum(order.tax_amount))}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total Value</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{formatIndianNumber(toNum(order.total_value))}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Scope Lines
              </Typography>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  {['Description', 'Qty', 'UOM', 'Rate', 'SAC Code', 'Amount'].map((h, i) => (
                    <TableCell key={h} align={i === 1 || i === 3 || i === 5 ? 'right' : 'left'} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {order.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ border: 0 }}>
                      <TableEmptyState title="No scope lines on this work order" />
                    </TableCell>
                  </TableRow>
                ) : (
                  order.items.map((it, idx) => (
                    <TableRow key={it.id ?? idx} sx={bodyRowSx}>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {it.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {toNum(it.quantity)}
                      </TableCell>
                      <TableCell>{it.uom || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatIndianNumber(toNum(it.rate))}
                      </TableCell>
                      <TableCell>{it.sac_code || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatIndianNumber(toNum(it.amount ?? toNum(it.quantity) * toNum(it.rate)))}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableSurface>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TableSurface>
            <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Linked Service Entry Sheets
              </Typography>
              <Button
                size="small"
                startIcon={<Description />}
                component={RouterLink}
                to={`/procurement/service-entries/new?swoId=${id}`}
              >
                Create Service Entry
              </Button>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  {['SES', 'Status', 'Service Period', 'Total'].map((h, i) => (
                    <TableCell key={h} align={i === 3 ? 'right' : 'left'} sx={headerCellSx}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sheets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ border: 0 }}>
                      <TableEmptyState
                        title="No service entry sheets recorded yet"
                        hint="Delivered services must be entered here before this work order can be billed."
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  sheets.map((s) => (
                    <TableRow
                      key={s.id}
                      sx={{ ...bodyRowSx, cursor: 'pointer' }}
                      hover
                      onClick={() => navigate(`/procurement/service-entries/${s.id}`)}
                    >
                      <TableCell sx={{ py: 1.5 }}>
                        <CodeCell code={s.ses_number} tint="#7c3aed" icon={<Assignment fontSize="small" />} />
                      </TableCell>
                      <TableCell>
                        <StatusDot label={s.status} tone={SES_STATUS_TONE[s.status] || 'neutral'} />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {fmtDate(s.service_period_from)} – {fmtDate(s.service_period_to)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatIndianNumber(toNum(s.total_amount))}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableSurface>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <AttachmentsPanel entityType="service_work_order" entityId={order.id} title="Work Order Documents" />
        </Grid>
      </Grid>

      <ReasonDialog
        open={rejectOpen}
        title="Reject Service Work Order"
        helperText="The work order returns to draft and the reason is appended to its remarks."
        submitLabel={actionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
        loading={actionLoading === 'reject'}
        onClose={() => setRejectOpen(false)}
        onSubmit={handleReject}
      />

      <ReasonDialog
        open={cancelOpen}
        title="Cancel Service Work Order"
        helperText="This cancels the work order. This action cannot be undone."
        submitLabel={actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel Work Order'}
        loading={actionLoading === 'cancel'}
        onClose={() => setCancelOpen(false)}
        onSubmit={handleCancel}
      />
    </Box>
  );
};

export default ServiceWorkOrderDetailPage;
